/* istanbul ignore file */
import { STRINGS } from 'config';
import React from 'react';
import {
  addNewTemplate,
  createSectionHeadings,
  mapPractice,
  setShowTemplateValidation,
  updateTemplate,
  updateTemplateContent,
  updateTemplateName,
  updateTemplatePracticeRole,
  updateTemplateTranscriptionType,
  updateTemplateType,
} from 'store/action/templates';
import { useAppDispatch, useAppState } from 'store/configureStore';
import {
  CONTENT_MAX_LENGTH,
  CONTENT_MIN_LENGTH,
  NAME_MAX_LENGTH,
  NAME_MIN_LENGTH,
  PRACTICE_ROLE_MAX_LENGTH,
  PRACTICE_ROLE_MIN_LENGTH,
  getContentErrorMessage,
  getNameErrorMessage,
  getRoleErrorMessage,
  visibilityTypes,
} from 'store/reducer/templates';
import { ContentFields, TranscriptionTypeFields, VisibilityFields } from './FormItems';
import {
  DeleteTemplateButton,
  MainFooter,
  NoTemplateSelectedText,
  PracticeRoleInfo,
  SaveTemplateButton,
  TemplateManagerMainContent,
  TextInput,
} from './styles';

const { PENDING } = STRINGS.REQUEST_STATUS;

type GetIsTemplateValidFn = (
  nameLength: number,
  contentLength: number,
  roleLength: number,
  hasType: boolean,
) => boolean;

const getIsTemplateValid: GetIsTemplateValidFn = (nameLength, contentLength, roleLength, hasType) =>
  nameLength >= NAME_MIN_LENGTH &&
  nameLength <= NAME_MAX_LENGTH &&
  contentLength >= CONTENT_MIN_LENGTH &&
  contentLength <= CONTENT_MAX_LENGTH &&
  roleLength >= PRACTICE_ROLE_MIN_LENGTH &&
  roleLength <= PRACTICE_ROLE_MAX_LENGTH &&
  hasType;

interface PreviewProps {
  idPrefix: string;
  templateId: string;
  onSaveNewTemplate: (content: string) => void;
  isAddingNewTemplate: boolean;
  setIsConfirmDeleteTemplateOpen: (open: boolean) => void;
  templateToDelete: string | null;
}

interface Template {
  name: string;
  sectionHeadings: string[];
  visibility: visibilityTypes;
  practiceRole: string;
  transcriptionType: 'Consultation' | 'Dictation';
  id?: string;
}

export const Preview: React.FC<PreviewProps> = props => {
  const {
    idPrefix,
    templateId,
    onSaveNewTemplate,
    isAddingNewTemplate,
    setIsConfirmDeleteTemplateOpen,
    templateToDelete,
  } = props;
  const dispatch = useAppDispatch();
  const {
    templates: {
      apiErrors,
      template: { id, content, name, type, practiceRole, transcriptionType },
      templateHasUnsavedChanges,
      templateSavingState,
      showTemplateValidation,
    },
    app: { selectedPractice },
  } = useAppState();
  const isTemplateDeleting = false;
  const isTypeSelected = type !== null;
  const showValidation = showTemplateValidation;
  const nameErrorMessage = getNameErrorMessage(name, apiErrors, type, showValidation);
  const roleErrorMessage = getRoleErrorMessage(practiceRole.length);
  const contentErrorMessage = getContentErrorMessage(content.length);
  const selectedPracticeId = selectedPractice?.id;
  const isEmisTemplate = name === STRINGS.TEMPLATE.EMIS_WEB_TEMPLATE;

  const handleChangeContent = (event: React.ChangeEvent<HTMLTextAreaElement>) =>
    dispatch(updateTemplateContent(event.target.value));
  const handleSaveTemplateChanges = () => {
    if (selectedPracticeId) {
      const isTemplateValid = getIsTemplateValid(name.length, content.length, practiceRole.length, type !== null);
      dispatch(setShowTemplateValidation(!isTemplateValid));
      if (!isTemplateValid) {
        return null;
      }
      const template: Template = {
        name: name.trim(),
        sectionHeadings: createSectionHeadings(content),
        visibility: mapPractice(type) as visibilityTypes,
        practiceRole: practiceRole.trim(),
        transcriptionType,
      };
      if (isAddingNewTemplate) {
        addNewTemplate(template, dispatch, selectedPracticeId);
        return onSaveNewTemplate(content);
      }
      return updateTemplate({ ...template, id }, dispatch, selectedPracticeId);
    }
    return null;
  };
  const handleUpdateTemplateType = (_templateType: string) => dispatch(updateTemplateType(_templateType));
  const prInfoText = practiceRole?.length ? practiceRole : '[practice role]';
  const transcriptionText =
    transcriptionType === 'Consultation' ? (
      <>
        A transcribed conversation between a <strong>{prInfoText}</strong> and a patient
      </>
    ) : (
      <>
        A dictated transcription recorded by a <strong>{prInfoText}</strong>
      </>
    );
  const saveOrUpdateTemplateText = isAddingNewTemplate ? 'Save template' : ' Update template';
  return (
    <>
      <TemplateManagerMainContent isEmpty={!templateId}>
        <TextInput
          label="Template name"
          placeholder="Enter a unique template name"
          id={`${idPrefix}TemplateNameInput`}
          value={name}
          automationName={`${idPrefix}TemplateNameInput`}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => dispatch(updateTemplateName(event.target.value))}
          state={showValidation && (nameErrorMessage ? 'error' : null)}
          message={nameErrorMessage}
          disabled={isEmisTemplate}
        />
        <TranscriptionTypeFields
          transcriptionType={transcriptionType}
          setTranscriptionType={type => dispatch(updateTemplateTranscriptionType(type))}
          idPrefix={idPrefix}
          disabled={isEmisTemplate}
        />
        <div>
          <TextInput
            label="Practice role"
            placeholder="Who this template is for (eg. GP, Nurse)"
            id={`${idPrefix}TemplatePracticeRoleInput`}
            value={practiceRole}
            automationName={`${idPrefix}TemplatePracticeRoleInput`}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              dispatch(updateTemplatePracticeRole(event.target.value))
            }
            state={showValidation && (roleErrorMessage ? 'error' : null)}
            message={showValidation ? roleErrorMessage : ''}
            disabled={isEmisTemplate}
          />
          <PracticeRoleInfo>{transcriptionText}</PracticeRoleInfo>
        </div>
        <ContentFields
          content={content}
          onChangeContent={handleChangeContent}
          idPrefix={idPrefix}
          contentErrorMessage={showValidation ? contentErrorMessage : undefined}
          disabled={isEmisTemplate}
        />
        <VisibilityFields
          templateType={type || 'Personal'} // fix
          setTemplateType={handleUpdateTemplateType}
          idPrefix={idPrefix}
          showError={showValidation && !isTypeSelected}
          disabled={isEmisTemplate}
        />
      </TemplateManagerMainContent>
      <MainFooter>
        {templateId !== 'newTemplate' && !isEmisTemplate && (
          <DeleteTemplateButton
            data-automation="DeleteTemplateButton"
            onClick={() => setIsConfirmDeleteTemplateOpen(true)}
            disabled={templateSavingState === PENDING}
          >
            {isTemplateDeleting && templateToDelete === templateId ? 'Deleting...' : 'Delete'}
          </DeleteTemplateButton>
        )}
        {!isEmisTemplate && (
          <SaveTemplateButton
            onClick={handleSaveTemplateChanges}
            data-automation="ConfirmUpdateTemplateButton"
            disabled={!templateHasUnsavedChanges || templateSavingState === PENDING || name === 'Default template'}
          >
            {templateSavingState === PENDING ? 'Saving...' : saveOrUpdateTemplateText}
          </SaveTemplateButton>
        )}
      </MainFooter>
    </>
  );
};

export const NoPreview = () => (
  <>
    <TemplateManagerMainContent isEmpty>
      <NoTemplateSelectedText>No template selected</NoTemplateSelectedText>
    </TemplateManagerMainContent>
    <MainFooter />
  </>
);
